import React from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography, Paper } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
// LAP-733 (GE022) — translate the home view header.
import { useTranslation } from 'react-i18next';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '12px',
  padding: '4px',
  '& .MuiToggleButtonGroup-grouped': {
    margin: theme.spacing(0.5),
    border: 0,
    borderRadius: '8px',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    '&:not(.Mui-selected)': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
  },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
  transition: 'all 0.3s ease',
  // Mobile-first: comfortable 44px touch target + compact padding so the
  // toggle never overflows the viewport. Roomier on tablet/desktop.
  minHeight: 44,
  padding: '10px 14px',
  fontSize: '13px',
  [theme.breakpoints.up('sm')]: {
    padding: '8px 20px',
    fontSize: '14px',
    minHeight: 'auto',
  },
}));

// Life Intelligence — bouton d'accès au chat IA généraliste multi-domaines
// (route /life-intelligence). Même langage visuel que le toggle de vue.
const LifeIntelligenceButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  color: '#c7b8f5',
  backgroundColor: 'rgba(139, 92, 246, 0.15)',
  border: '1px solid rgba(139, 92, 246, 0.35)',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  // Mobile-first : mêmes gabarits que StyledToggleButton pour rester aligné.
  minHeight: 44,
  padding: '10px 14px',
  fontSize: '13px',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.3)',
    borderColor: 'rgba(139, 92, 246, 0.6)',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '8px 20px',
    fontSize: '14px',
    minHeight: 'auto',
  },
}));

const HomeToggleHeader = ({ view, onViewChange }) => {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const handleChange = (event, newView) => {
    if (newView !== null) {
      onViewChange(newView);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #001B42 0%, #003366 100%)',
        borderRadius: 0,
        py: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 3 },
        display: 'flex',
        // On phones the title + toggle no longer fit side by side, which used
        // to push the toggle off-screen. Stack vertically below the sm breakpoint.
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 1.5, sm: 2 },
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        // position: 'sticky',
        // top: 64,
        zIndex: 1000,
        backdropFilter: 'blur(10px)',
      }}
    >
      <Box sx={{ minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
        <Typography
          variant="h5"
          sx={{
            color: '#fff',
            fontWeight: 600,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            fontSize: { xs: '1.15rem', sm: '1.5rem' },
          }}
        >
          {view === 'classic' ? t('home.title') : t('home.smartFeed')}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            display: 'block',
            mt: 0.5,
          }}
        >
          {view === 'classic'
            ? t('home.subtitle')
            : t('home.smartFeedSubtitle')}
        </Typography>
      </Box>

      {/* Groupe d'actions à droite : bouton Life Intelligence + toggle de vue.
          Regroupés dans UN SEUL enfant flex pour préserver la géométrie
          d'origine du bandeau (titre à gauche / actions à droite via
          `space-between`, exactement comme avant l'ajout du bouton).
          Sur mobile : empilés pleine largeur. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1.5, sm: 1.5 },
          flexShrink: 0,
        }}
      >
        <LifeIntelligenceButton
          startIcon={<AutoAwesomeIcon fontSize="small" />}
          onClick={() => navigate('/life-intelligence')}
          aria-label="life intelligence"
          sx={{
            flexShrink: 0,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {t('home.lifeIntelligence', { defaultValue: 'Life Intelligence' })}
        </LifeIntelligenceButton>

        <StyledToggleButtonGroup
          value={view}
          exclusive
          onChange={handleChange}
          aria-label="view toggle"
          sx={{
            flexShrink: 0,
            // Full width on phones so both buttons stay on-screen and tappable.
            width: { xs: '100%', sm: 'auto' },
            '& .MuiToggleButtonGroup-grouped': {
              flex: { xs: 1, sm: 'none' },
            },
          }}
        >
          <StyledToggleButton value="classic" aria-label="classic view">
            <ViewModuleIcon fontSize="small" />
            {t('home.classic')}
          </StyledToggleButton>
          <StyledToggleButton value="smartfeed" aria-label="smart feed view">
            <DynamicFeedIcon fontSize="small" />
            {t('home.smartFeed')}
          </StyledToggleButton>
        </StyledToggleButtonGroup>
      </Box>
    </Paper>
  );
};

export default HomeToggleHeader;
